import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { PackageCheck, RotateCcw, Search, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "react-toastify";

import PaginationComponent from "../components/PaginationComponent";
import {
  type DeadStockProduct,
  useGetDeadStockProducts,
  useReceiveDeadStockReplacement,
  useWriteOffDeadStock,
} from "../service/deadStockService";
import { useDownloadDamagedProductsExcel } from "../service/warrantyService";
import { Button } from "../utils/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../utils/components/ui/dialog";
import { Input } from "../utils/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../utils/components/ui/select";
import { formatAppDate } from "../utils/dateFormatter";

type BooleanFilter = "all" | "true" | "false";

const PAGE_SIZE_OPTIONS = [10, 20, 30, 50, 100];

const formatDisplayDate = (value: string) => {
  return formatAppDate(value);
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(value || 0);

const toApiDateTime = (value: string, endOfDay = false) => {
  if (!value) return undefined;
  return `${value}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}Z`;
};

const toBooleanParam = (value: BooleanFilter) =>
  value === "all" ? undefined : value === "true";

function DeadStockManagement() {
  const queryClient = useQueryClient();
  const branchId = Number(localStorage.getItem("branchId")) || undefined;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [saleId, setSaleId] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [claimId, setClaimId] = useState("");
  const [claimNumber, setClaimNumber] = useState("");
  const [isSettled, setIsSettled] = useState<BooleanFilter>("all");
  const [replacementReceived, setReplacementReceived] =
    useState<BooleanFilter>("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedProduct, setSelectedProduct] =
    useState<DeadStockProduct | null>(null);
  const [replacementQuantity, setReplacementQuantity] = useState("");

  const [writeOffProduct, setWriteOffProduct] = useState<DeadStockProduct | null>(null);

  type WriteOffFormInputs = {
    writeOffDate: string;
    reason: string;
    notes: string;
  };

  const {
    register: registerWriteOff,
    handleSubmit: handleWriteOffSubmit,
    reset: resetWriteOffForm,
    formState: { errors: writeOffErrors },
  } = useForm<WriteOffFormInputs>({
    defaultValues: {
      writeOffDate: new Date().toISOString().split("T")[0],
      reason: "",
      notes: "",
    },
  });

  useEffect(() => {
    setPage(0);
  }, [
    saleId,
    invoiceNo,
    claimId,
    claimNumber,
    isSettled,
    replacementReceived,
    fromDate,
    toDate,
    pageSize,
  ]);

  const queryParams = useMemo(
    () => ({
      saleId: saleId ? Number(saleId) : undefined,
      invoiceNo: invoiceNo.trim() || undefined,
      branchId,
      claimId: claimId ? Number(claimId) : undefined,
      claimNumber: claimNumber.trim() || undefined,
      isSettled: toBooleanParam(isSettled),
      replacementReceived: toBooleanParam(replacementReceived),
      fromDate: toApiDateTime(fromDate),
      toDate: toApiDateTime(toDate, true),
      page,
      pageSize,
    }),
    [
      branchId,
      claimId,
      claimNumber,
      fromDate,
      invoiceNo,
      isSettled,
      page,
      pageSize,
      replacementReceived,
      saleId,
      toDate,
    ],
  );

  const { data, isLoading, isFetching } = useGetDeadStockProducts(queryParams);
  const receiveReplacement = useReceiveDeadStockReplacement();

  const rows = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const { mutate: downloadExcel, isPending: isDownloading } = useDownloadDamagedProductsExcel();

  const handleExport = () => {
    downloadExcel(queryParams);
  };

  const handleOpenReplacement = (row: DeadStockProduct) => {
    setSelectedProduct(row);
    setReplacementQuantity(
      row.replacementQuantity ? String(row.replacementQuantity) : "",
    );
  };

  const handleCloseReplacement = () => {
    setSelectedProduct(null);
    setReplacementQuantity("");
  };

  const handleSubmitReplacement = () => {
    if (!selectedProduct) return;

    const quantity = Number(replacementQuantity);

    if (Number.isNaN(quantity) || quantity <= 0) {
      toast.error("Replacement quantity must be greater than zero.");
      return;
    }

    receiveReplacement.mutate(
      {
        id: selectedProduct.id,
        quantity,
        unit: selectedProduct.unit,
        conversionToBaseUnit: selectedProduct.conversionToBaseUnit || 1,
        baseQuantity:
          quantity * Number(selectedProduct.conversionToBaseUnit || 1),
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["useGetDeadStockProducts"],
          });
          toast.success("Replacement received successfully.");
          handleCloseReplacement();
        },
        onError: (error: Error) => {
          toast.error(`Unable to receive replacement: ${error.message}`);
        },
      },
    );
  };

  const writeOffMutation = useWriteOffDeadStock();

  const handleOpenWriteOff = (row: DeadStockProduct) => {
    setWriteOffProduct(row);
    resetWriteOffForm({
      writeOffDate: new Date().toISOString().split("T")[0],
      reason: "",
      notes: "",
    });
  };

  const handleCloseWriteOff = () => {
    setWriteOffProduct(null);
    resetWriteOffForm();
  };

  const onSubmitWriteOff = (data: WriteOffFormInputs) => {
    if (!writeOffProduct) return;

    writeOffMutation.mutate(
      {
        writeOffDate: data.writeOffDate,
        reason: data.reason,
        notes: data.notes,
        damagedProductIds: [writeOffProduct.id],
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["useGetDeadStockProducts"],
          });
          toast.success("Dead stock written off successfully.");
          handleCloseWriteOff();
        },
        onError: (error: Error) => {
          toast.error(`Unable to write off: ${error.message}`);
        },
      }
    );
  };

  const clearFilters = () => {
    setSaleId("");
    setInvoiceNo("");
    setClaimId("");
    setClaimNumber("");
    setIsSettled("all");
    setReplacementReceived("all");
    setFromDate("");
    setToDate("");
  };

  return (
    <>
      <div className="flex min-h-full flex-col gap-6 pb-4">
        <section className="rounded-[28px] border border-[#DDE7FB] bg-[linear-gradient(180deg,#FFFFFF_0%,#F5F9FF_100%)] p-6 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.35)]">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                Dead Stock Management
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                Track complaint products and record replacement receipts.
                {isFetching ? " Updating..." : ""}
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#DBEAFE] bg-[#EFF6FF] px-4 py-2 text-sm font-semibold text-[#1D4ED8]">
              <PackageCheck className="h-4 w-4" />
              {totalElements} complaint products
            </div>
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={invoiceNo}
                onChange={(event) => setInvoiceNo(event.target.value)}
                placeholder="Invoice number"
                className="h-12 rounded-2xl border-[#E6ECF5] bg-[#F8FAFC] pl-11"
              />
            </div>
            <Input
              type="number"
              value={saleId}
              onChange={(event) => setSaleId(event.target.value)}
              placeholder="Sale ID"
              className="h-12 rounded-2xl border-[#E6ECF5] bg-[#F8FAFC]"
            />
            <Input
              type="number"
              value={claimId}
              onChange={(event) => setClaimId(event.target.value)}
              placeholder="Claim ID"
              className="h-12 rounded-2xl border-[#E6ECF5] bg-[#F8FAFC]"
            />
            <Input
              value={claimNumber}
              onChange={(event) => setClaimNumber(event.target.value)}
              placeholder="Claim number"
              className="h-12 rounded-2xl border-[#E6ECF5] bg-[#F8FAFC]"
            />
            <Select
              value={isSettled}
              onValueChange={(value) => setIsSettled(value as BooleanFilter)}
            >
              <SelectTrigger className="h-12 w-full rounded-2xl border-[#E6ECF5] bg-[#F8FAFC]">
                <SelectValue placeholder="Settlement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Settlement</SelectItem>
                <SelectItem value="true">Settled</SelectItem>
                <SelectItem value="false">Pending</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={replacementReceived}
              onValueChange={(value) =>
                setReplacementReceived(value as BooleanFilter)
              }
            >
              <SelectTrigger className="h-12 w-full rounded-2xl border-[#E6ECF5] bg-[#F8FAFC]">
                <SelectValue placeholder="Replacement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Replacement</SelectItem>
                <SelectItem value="true">Received</SelectItem>
                <SelectItem value="false">Not Received</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              className="h-12 rounded-2xl border-[#E6ECF5] bg-[#F8FAFC]"
            />
            <Input
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              className="h-12 rounded-2xl border-[#E6ECF5] bg-[#F8FAFC]"
            />
          </div>

          <div className="mt-4 flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleExport}
              disabled={isDownloading}
              className="h-11 rounded-xl border-[#BFD1FF] px-5 text-[#1A5EF9] hover:bg-[#EEF4FF] hover:text-[#0E4FDE] flex items-center gap-2"
            >
              {isDownloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="w-4 h-4" />
              )}
              Export to Excel
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={clearFilters}
              className="h-11 rounded-xl border-[#BFD1FF] px-5 text-[#1A5EF9] hover:bg-[#EEF4FF] hover:text-[#0E4FDE]"
            >
              Reset
            </Button>
          </div>
        </section>

        <div className="flex flex-1 flex-col overflow-hidden rounded-[28px] border border-[#DDE7FB] bg-white shadow-[0_18px_40px_-32px_rgba(15,23,42,0.45)]">
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="min-w-[1180px] w-full divide-y divide-[#EEF2F6]">
              <thead className="sticky top-0 z-10 bg-[#F8FAFC]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Claim
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Invoice
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Complaint Qty
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Refund
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Settlement
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Reported
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF2F6] bg-white">
                {rows.length > 0 ? (
                  rows.map((row) => (
                    <tr key={row.id} className="transition hover:bg-[#FAFCFF]">
                      <td className="px-6 py-5">
                        <div className="font-semibold text-slate-900">
                          {row.claimNumber || `#${row.claimId}`}
                        </div>
                        <div className="mt-1 text-xs text-slate-400">
                          Claim ID: {row.claimId}
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="font-medium text-slate-700">
                          {row.invoiceNumber}
                        </div>
                        <div className="mt-1 text-xs text-slate-400">
                          Sale ID: {row.saleId}
                        </div>
                      </td>
                      <td className="px-6 py-5 text-right text-sm font-semibold text-slate-900">
                        {row.quantity}
                      </td>
                      <td className="px-6 py-5 text-right text-sm font-semibold text-slate-900">
                        {formatCurrency(row.refundAmount)}
                      </td>
                      <td className="px-6 py-5 text-center">
                        <StatusBadge
                          isPositive={row.isSettled}
                          positiveLabel="Settled"
                          negativeLabel="Pending"
                        />
                      </td>
                      <td className="px-6 py-5 text-sm text-slate-600">
                        {formatDisplayDate(row.reportedAt)}
                      </td>
                      <td className="px-6 py-5 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            onClick={() => handleOpenReplacement(row)}
                            disabled={row.replacementReceived}
                            className="h-10 rounded-xl bg-[#1A5EF9] px-4 text-white hover:bg-[#0E4FDE] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
                          >
                            <RotateCcw className="h-4 w-4" />
                            {row.replacementReceived ? "Received" : "Receive"}
                          </Button>
                          {localStorage.getItem("role") === "ADMIN" && (
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => handleOpenWriteOff(row)}
                              className="h-10 rounded-xl border-[#BFD1FF] px-4 text-[#1A5EF9] hover:bg-[#EEF4FF] hover:text-[#0E4FDE]"
                            >
                              Write Off
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-6 py-12 text-center text-sm text-slate-500"
                    >
                      {isLoading
                        ? "Loading complaint products..."
                        : "No complaint products match the selected filters."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col gap-4 border-t border-[#EEF2F6] bg-[linear-gradient(180deg,#FFFFFF_0%,#FBFDF8_100%)] px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-slate-500">
              <span className="font-semibold text-slate-900">
                {totalElements}
              </span>{" "}
              complaint products found
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Rows per page:</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => setPageSize(Number(value))}
                >
                  <SelectTrigger className="h-10 w-[88px] rounded-xl border-[#E6ECF5] bg-[#F8FAFC] px-3 text-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <SelectItem key={size} value={String(size)}>
                        {size}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {totalPages > 0 && (
                <PaginationComponent
                  currentPage={data?.pageNumber ?? page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={!!selectedProduct}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseReplacement();
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="max-w-[520px] rounded-[28px] border border-[#E8EDF3] bg-white p-0 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.45)]"
        >
          <div className="p-6 sm:p-8">
            <DialogHeader className="text-left">
              <DialogTitle className="text-2xl font-semibold tracking-tight text-[#1A5EF9]">
                Receive Replacement
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm text-slate-500">
                {selectedProduct?.claimNumber || "Complaint product"}{" "}
                replacement quantity
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 space-y-2">
              <label className="text-sm font-medium text-slate-600">
                Quantity
              </label>
              <Input
                type="number"
                min="0.0001"
                step="any"
                value={replacementQuantity}
                onChange={(event) => setReplacementQuantity(event.target.value)}
                placeholder="Enter quantity"
                className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
              />
            </div>

            <DialogFooter className="mt-8 border-t border-[#E8EDF3] pt-6 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseReplacement}
                className="h-11 min-w-[112px] rounded-xl border-[#D8E1EC] px-6 text-slate-600"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleSubmitReplacement}
                disabled={receiveReplacement.isPending}
                className="h-11 min-w-[112px] rounded-xl bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE]"
              >
                {receiveReplacement.isPending ? "Submitting..." : "Submit"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!writeOffProduct}
        onOpenChange={(open) => {
          if (!open) {
            handleCloseWriteOff();
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="max-w-[520px] rounded-[28px] border border-[#E8EDF3] bg-white p-0 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.45)]"
        >
          <div className="p-6 sm:p-8">
            <DialogHeader className="text-left">
              <DialogTitle className="text-2xl font-semibold tracking-tight text-[#1A5EF9]">
                Write Off Dead Stock
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm text-slate-500">
                Write off {writeOffProduct?.claimNumber || "complaint product"}. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleWriteOffSubmit(onSubmitWriteOff)} className="mt-6 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Write-off Date <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  {...registerWriteOff("writeOffDate", { required: "Date is required" })}
                  className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
                {writeOffErrors.writeOffDate && (
                  <p className="text-red-500 text-xs">{writeOffErrors.writeOffDate.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Reason <span className="text-red-500">*</span>
                </label>
                <Input
                  placeholder="e.g. Broken beyond repair"
                  {...registerWriteOff("reason", { required: "Reason is required" })}
                  className="h-12 rounded-xl border-[#E6ECF5] bg-[#F8FAFC]"
                />
                {writeOffErrors.reason && (
                  <p className="text-red-500 text-xs">{writeOffErrors.reason.message}</p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-600">
                  Notes
                </label>
                <textarea
                  placeholder="Additional details..."
                  {...registerWriteOff("notes")}
                  className="w-full min-h-[80px] p-3 text-sm rounded-xl border border-[#E6ECF5] bg-[#F8FAFC] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>

              <DialogFooter className="mt-8 border-t border-[#E8EDF3] pt-6 sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseWriteOff}
                  className="h-11 min-w-[112px] rounded-xl border-[#D8E1EC] px-6 text-slate-600"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={writeOffMutation.isPending}
                  className="h-11 min-w-[112px] rounded-xl bg-[#1A5EF9] px-6 text-white hover:bg-[#0E4FDE]"
                >
                  {writeOffMutation.isPending ? "Submitting..." : "Submit"}
                </Button>
              </DialogFooter>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatusBadge({
  isPositive,
  positiveLabel,
  negativeLabel,
}: {
  isPositive: boolean;
  positiveLabel: string;
  negativeLabel: string;
}) {
  return (
    <span
      className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
        isPositive
          ? "border-[#D9F2E5] bg-[#EFFAF4] text-[#0E9F6E]"
          : "border-[#FFE7C2] bg-[#FFF8EC] text-[#D97706]"
      }`}
    >
      {isPositive ? positiveLabel : negativeLabel}
    </span>
  );
}

export default DeadStockManagement;
